import {
  Badge,
  Button,
  Checkbox,
  ConfirmDialog,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Spinner,
  Switch,
} from '@repo/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  AlertCircle,
  Archive,
  BarChart3,
  Check,
  Pause,
  Pencil,
  Play,
  Tags,
  Trash2,
  UserPlus,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { CadenceCostCard } from '@/components/cadences/cadence-cost-card'
import {
  CadenceFormDialog,
  type CadenceFormValues,
} from '@/components/cadences/CadenceFormDialog'
import { useAgents } from '@/hooks/use-agents'
import { useAuth } from '@/hooks/use-auth'
import {
  useBackfillCadenceEntryTags,
  useCadence,
  useCadenceEnrollments,
  useCadenceMetrics,
  useCadenceSteps,
  useDeleteCadence,
  useUpdateCadence,
  useUpdateCadenceEntryTags,
} from '@/hooks/use-cadences'
import { useChannels } from '@/hooks/use-channels'
import { useTags } from '@/hooks/use-leads'
import { useDepartments, useOrgMembers } from '@/hooks/use-settings'
import type { CadenceEnrollmentStatus, CadenceStatus } from '@/types/cadences'

export const Route = createFileRoute('/_app/cadencias/$cadenceId/')({
  component: CadenceOverview,
})

const STATUS_LABEL: Record<CadenceStatus, string> = {
  DRAFT: 'Rascunho',
  ACTIVE: 'Ativa',
  PAUSED: 'Pausada',
  ARCHIVED: 'Arquivada',
}

function CadenceOverview() {
  const { cadenceId } = Route.useParams()
  const navigate = useNavigate()
  const { currentOrgId } = useAuth()
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const {
    data: cadence,
    isLoading,
    isError,
  } = useCadence(currentOrgId, cadenceId)
  const { data: steps = [] } = useCadenceSteps(currentOrgId, cadenceId)
  const { data: enrollmentsResponse } = useCadenceEnrollments(
    currentOrgId,
    cadenceId
  )
  const { data: channels = [] } = useChannels(currentOrgId)
  const { data: agentsResponse } = useAgents(currentOrgId)
  const { data: departments = [] } = useDepartments(currentOrgId)
  const { data: membersResponse } = useOrgMembers(currentOrgId)
  const { data: tags = [] } = useTags(currentOrgId)
  const { data: deliveryMetrics } = useCadenceMetrics(currentOrgId, cadenceId)
  const updateCadence = useUpdateCadence(currentOrgId, cadenceId)
  const updateEntryTags = useUpdateCadenceEntryTags(currentOrgId, cadenceId)
  const backfillEntryTags = useBackfillCadenceEntryTags(currentOrgId, cadenceId)
  const deleteCadence = useDeleteCadence(currentOrgId)
  const enrollments = enrollmentsResponse?.data ?? []
  const agents = agentsResponse?.data ?? []
  const members = membersResponse?.data ?? []
  const canActivate = steps.some((step) =>
    step.type === 'MEDIA' ? !!step.media_url : step.content.trim()
  )

  const metrics = {
    total: enrollments.length,
    active: countStatus(enrollments, 'ACTIVE'),
    completed: countStatus(enrollments, 'COMPLETED'),
    failed: countStatus(enrollments, 'FAILED'),
  }

  async function setStatus(status: CadenceStatus) {
    if (!cadence) return

    if (status === 'ACTIVE') {
      if (!canActivate) {
        toast.error('Adicione ao menos uma etapa com conteúdo antes de ativar')
        return
      }
      if (
        cadence.channelStrategy === 'SPECIFIC_CHANNELS' &&
        cadence.channel_ids.length === 0
      ) {
        toast.error('Selecione ao menos um canal antes de ativar a cadência')
        return
      }
    }
    await updateCadence.mutateAsync({ status })
    toast.success('Status atualizado')
  }

  async function saveDetails(input: CadenceFormValues) {
    await updateCadence.mutateAsync({
      name: input.name,
      description: input.description || undefined,
      channelStrategy: input.channel_strategy,
      channel_ids: input.channel_ids,
    })
    toast.success('Cadência atualizada')
    setEditOpen(false)
  }

  async function remove() {
    await deleteCadence.mutateAsync(cadenceId)
    toast.success('Cadência excluída')
    void navigate({ to: '/cadencias' })
  }

  async function toggleEntryTag(tagId: string) {
    if (!cadence) return
    const current = cadence.required_tag_ids ?? []
    const next = current.includes(tagId)
      ? current.filter((id) => id !== tagId)
      : [...current, tagId]
    await updateEntryTags.mutateAsync(next)
    toast.success('Tags de entrada atualizadas')
  }

  async function backfill() {
    const result = await backfillEntryTags.mutateAsync()
    toast.success(
      `Inscritos: ${result.enrolled} · Bloqueados: ${result.blocked} · Verificados: ${result.scanned}`
    )
  }

  async function updateRouting(data: {
    department_id?: string | null
    agent_id?: string | null
    default_assignee_id?: string | null
  }) {
    await updateCadence.mutateAsync(data)
    toast.success('Roteamento atualizado')
  }

  async function toggleConcurrent(value: boolean) {
    await updateCadence.mutateAsync({ allowConcurrent: value })
    toast.success('Regra de concorrência atualizada')
  }

  async function toggleEnrollOnce(value: boolean) {
    await updateCadence.mutateAsync({ enrollOncePerContact: value })
    toast.success('Regra de disparo único atualizada')
  }

  async function toggleChannel(channelId: string) {
    if (!cadence) return
    const current = cadence.channel_ids ?? []
    const next = current.includes(channelId)
      ? current.filter((id) => id !== channelId)
      : [...current, channelId]
    await updateCadence.mutateAsync({
      channelStrategy: 'SPECIFIC_CHANNELS',
      channel_ids: next,
    })
    toast.success('Canais atualizados')
  }

  if (isError) {
    return (
      <div className="border-destructive/30 bg-destructive/10 text-destructive flex items-center gap-2 rounded-lg border px-4 py-3 text-sm">
        <AlertCircle className="h-4 w-4" />
        Erro ao carregar a cadência.
      </div>
    )
  }

  if (isLoading || !cadence) {
    return <Skeleton className="h-80 w-full rounded-lg" />
  }

  return (
    <div className="space-y-6">
      <section className="bg-card border-border rounded-xl border p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className="truncate text-base font-semibold tracking-tight">
                {cadence.name}
              </h2>
              <Badge
                variant={cadence.status === 'ACTIVE' ? 'default' : 'outline'}
              >
                {STATUS_LABEL[cadence.status]}
              </Badge>
            </div>
            <p className="text-muted-foreground mt-1 max-w-2xl text-sm">
              {cadence.description || 'Sem descrição.'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditOpen(true)}
            >
              <Pencil className="h-4 w-4" />
              Editar
            </Button>
            {cadence.status === 'DRAFT' ? (
              <Button type="button" onClick={() => void setStatus('ACTIVE')}>
                <Play className="h-4 w-4" />
                Ativar
              </Button>
            ) : null}
            {cadence.status === 'ACTIVE' ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => void setStatus('PAUSED')}
              >
                <Pause className="h-4 w-4" />
                Pausar
              </Button>
            ) : null}
            {cadence.status === 'PAUSED' ? (
              <>
                <Button type="button" onClick={() => void setStatus('ACTIVE')}>
                  <Play className="h-4 w-4" />
                  Reativar
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void setStatus('ARCHIVED')}
                >
                  <Archive className="h-4 w-4" />
                  Arquivar
                </Button>
              </>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 className="text-destructive h-4 w-4" />
            </Button>
          </div>
        </div>

        {!canActivate && cadence.status !== 'ACTIVE' ? (
          <div className="border-warning/30 bg-warning/10 text-warning mt-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Para ativar esta cadência, adicione pelo menos uma etapa com
              conteúdo na aba <strong>Etapas</strong>.
            </span>
          </div>
        ) : null}
      </section>

      <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <MetricCard label="Etapas" value={steps.length} />
        <MetricCard label="Leads ativos" value={metrics.active} />
        <MetricCard
          label="Pausados"
          value={countStatus(enrollments, 'PAUSED')}
        />
        <MetricCard label="Concluídas" value={metrics.completed} />
      </section>

      <section className="bg-card border-border rounded-xl border p-5">
        <h3 className="mb-1 text-sm font-medium">Roteamento da cadência</h3>
        <p className="text-muted-foreground mb-4 text-xs">
          Define para onde os leads desta cadência vão quando uma conversa for
          criada pelo envio.
        </p>
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <p className="mb-1 text-xs font-medium">Departamento</p>
            <Select
              value={cadence.department_id ?? '_none'}
              onValueChange={(value) =>
                void updateRouting({
                  department_id: value === '_none' ? null : value,
                })
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Nenhum" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Nenhum</SelectItem>
                {departments.map((department) => (
                  <SelectItem key={department.id} value={department.id}>
                    {department.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="mb-1 text-xs font-medium">Agente de IA</p>
            <Select
              value={cadence.agent_id ?? '_none'}
              onValueChange={(value) =>
                void updateRouting({
                  agent_id: value === '_none' ? null : value,
                })
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Nenhum" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Nenhum</SelectItem>
                {agents.map((agent) => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="mb-1 text-xs font-medium">Atendente responsável</p>
            <Select
              value={cadence.default_assignee_id ?? '_none'}
              onValueChange={(value) =>
                void updateRouting({
                  default_assignee_id: value === '_none' ? null : value,
                })
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Nenhum" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Nenhum</SelectItem>
                {members.map((member) => (
                  <SelectItem key={member.id} value={member.userId}>
                    {member.user.name ?? member.user.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      <section className="bg-card border-border rounded-xl border p-5">
        <h3 className="mb-3 text-sm font-medium">Canais de envio</h3>
        {channels.length === 0 ? (
          <p className="text-muted-foreground text-xs">
            Nenhum canal cadastrado.
          </p>
        ) : (
          <div className="space-y-2">
            {channels.map((channel) => {
              const checked = (cadence.channel_ids ?? []).includes(channel.id)
              return (
                <label
                  key={channel.id}
                  className="hover:bg-muted/30 flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => void toggleChannel(channel.id)}
                  />
                  <span className="flex-1 truncate">{channel.name}</span>
                  <Badge
                    variant={
                      channel.status === 'CONNECTED' ? 'outline' : 'destructive'
                    }
                    className="text-2xs"
                  >
                    {channel.status === 'CONNECTED'
                      ? 'Conectado'
                      : 'Desconectado'}
                  </Badge>
                </label>
              )
            })}
          </div>
        )}
      </section>

      <section className="bg-card border-border rounded-xl border p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-medium">Concorrência</h3>
            <p className="text-muted-foreground mt-0.5 text-xs">
              Permite esta cadência iniciar e rodar em paralelo mesmo que o lead
              já esteja em outra cadência ativa ou pausada. O lead pode receber
              mensagens de mais de uma cadência ao mesmo tempo.
            </p>
          </div>
          <Switch
            checked={cadence.allowConcurrent}
            onCheckedChange={(value) => void toggleConcurrent(value)}
          />
        </div>
      </section>

      <section className="bg-card border-border rounded-xl border p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-medium">Disparo único por lead</h3>
            <p className="text-muted-foreground mt-0.5 text-xs">
              Cada lead entra nesta cadência uma única vez. Novas inscrições
              (por tag, webhook, agente de IA ou manual) são bloqueadas mesmo
              que a anterior já tenha sido concluída, cancelada ou falhado —
              para reenviar, use o reprocessar da inscrição existente.
            </p>
          </div>
          <Switch
            checked={cadence.enrollOncePerContact}
            onCheckedChange={(value) => void toggleEnrollOnce(value)}
          />
        </div>
      </section>

      <CadenceCostCard orgId={currentOrgId} cadenceId={cadenceId} />

      <section className="bg-card border-border rounded-xl border p-5">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-medium">
              <BarChart3 className="text-primary h-4 w-4" />
              Métricas de envio
            </h3>
            <p className="text-muted-foreground mt-1 text-xs">
              Resultado agregado a partir dos eventos registrados pela cadência.
            </p>
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-4">
          <InfoTile
            label="Tentativas"
            value={String(deliveryMetrics?.totals.attempts ?? 0)}
          />
          <InfoTile
            label="Enviadas"
            value={String(deliveryMetrics?.totals.sent ?? 0)}
          />
          <InfoTile
            label="Falhas"
            value={String(deliveryMetrics?.totals.failed ?? 0)}
          />
          <InfoTile
            label="Sucesso"
            value={`${Math.round((deliveryMetrics?.totals.successRate ?? 0) * 100)}%`}
          />
        </div>
        {deliveryMetrics?.perStep.length ? (
          <div className="mt-4 space-y-2">
            {deliveryMetrics.perStep.map((step) => (
              <div
                key={step.position}
                className="border-border rounded-lg border p-3"
              >
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="font-medium">Etapa {step.position + 1}</span>
                  <span className="text-muted-foreground text-xs">
                    {step.sent} enviadas · {step.failed} falhas ·{' '}
                    {step.attempts} tentativas
                  </span>
                </div>
                <div className="bg-muted mt-2 h-1.5 overflow-hidden rounded-full">
                  <div
                    className="bg-success/100 h-full"
                    style={{
                      width: `${
                        step.sent + step.failed > 0
                          ? Math.round(
                              (step.sent / (step.sent + step.failed)) * 100
                            )
                          : 0
                      }%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </section>

      <section className="bg-card border-border rounded-xl border p-5">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-medium">
              <Tags className="text-primary h-4 w-4" />
              Tags de entrada automática
            </h3>
            <p className="text-muted-foreground mt-1 text-xs">
              Quando um lead recebe uma dessas tags, ele entra automaticamente
              nesta cadência ativa.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={
              cadence.status !== 'ACTIVE' ||
              (cadence.required_tag_ids ?? []).length === 0 ||
              backfillEntryTags.isPending
            }
            onClick={() => void backfill()}
          >
            {backfillEntryTags.isPending ? (
              <Spinner className="h-3.5 w-3.5" />
            ) : (
              <UserPlus className="h-3.5 w-3.5" />
            )}
            Inscrever existentes
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {tags.length === 0 ? (
            <span className="text-muted-foreground text-xs">
              Nenhuma tag criada ainda.
            </span>
          ) : (
            tags.map((tag) => {
              const selected = (cadence.required_tag_ids ?? []).includes(tag.id)
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => void toggleEntryTag(tag.id)}
                  disabled={updateEntryTags.isPending}
                  className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition-colors ${
                    selected
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'hover:bg-muted text-muted-foreground'
                  }`}
                >
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: tag.color }}
                  />
                  {tag.name}
                  {selected ? <Check className="h-3 w-3" /> : null}
                </button>
              )
            })
          )}
        </div>
        {cadence.status !== 'ACTIVE' &&
        (cadence.required_tag_ids ?? []).length > 0 ? (
          <p className="text-warning mt-3 text-xs">
            Ative a cadência para receber inscrições automáticas por tag.
          </p>
        ) : null}
      </section>

      <CadenceFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        title="Editar cadência"
        channels={channels.map((ch) => ({ id: ch.id, name: ch.name }))}
        defaultValues={{
          name: cadence.name,
          description: cadence.description ?? '',
          channel_strategy: cadence.channelStrategy ?? 'CONTACT_LAST_CHANNEL',
          channel_ids: cadence.channel_ids ?? [],
        }}
        onSubmit={saveDetails}
      />
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Excluir cadência?"
        description="Essa ação remove a cadência e as inscrições vinculadas."
        confirmLabel="Excluir"
        destructive
        pending={deleteCadence.isPending}
        onConfirm={() => void remove()}
      />
    </div>
  )
}

function MetricCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-card border-border rounded-xl border p-4">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="mt-1 text-2xl font-semibold tracking-tight">{value}</p>
    </div>
  )
}

function InfoTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-border rounded-lg border p-3">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="mt-1 text-sm font-medium">{value}</p>
    </div>
  )
}

function countStatus(
  enrollments: Array<{ status: CadenceEnrollmentStatus }>,
  status: CadenceEnrollmentStatus
) {
  return enrollments.filter((enrollment) => enrollment.status === status).length
}
